import React, { useRef, useState } from 'react';
import Draggable, { DraggableEvent, DraggableData } from 'react-draggable';
import { Button, Grid, Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { useTranslation } from 'react-i18next';

const RAIL_HEIGHT = 65;
const RAIL_PADDING = 4;
const THUMB_WIDTH = 110;
// Fraction of the rail the thumb must travel before a scan is triggered
const TRIGGER_THRESHOLD = 0.5;

export default function SlideButton(props: any) {
    const { scanButtonText, machineActive, disableScanButton, setScanButtonPressed, isScanningCountdown } = props
    const [buttonPosition] = useState({ x: 0, y: 0 });
    const [dragX, setDragX] = useState(0);
    const [progress, setProgress] = useState(0);
    const [dragging, setDragging] = useState(false);
    const thumbRef = useRef<HTMLButtonElement>(null);
    const { t } = useTranslation();

    const getMaxTravel = (node: HTMLElement) => {
        const rail = node.parentElement;
        if (!rail) return 1;
        return Math.max(1, rail.clientWidth - RAIL_PADDING * 2 - node.offsetWidth);
    };

    const handleStart = () => {
        setDragging(true);
    };

    const handleDrag = (e: DraggableEvent, data: DraggableData) => {
        setDragX(data.x);
        setProgress(Math.min(1, data.x / getMaxTravel(data.node)));
    };

    const handleStop = (e: DraggableEvent, data: DraggableData) => {
        if (data.x / getMaxTravel(data.node) >= TRIGGER_THRESHOLD) {
            setScanButtonPressed(true)
        }
        setDragging(false);
        setDragX(0);
        setProgress(0);
    };

    const snapBack = dragging ? 'none' : 'all 0.25s ease-out';

    const styles = {
        display: {
            display: {
                xs: 'block',
                sm: 'none'
            }
        },
        rail: {
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            height: `${RAIL_HEIGHT}px`,
            p: `${RAIL_PADDING}px`,
            boxSizing: 'border-box',
            borderRadius: 1,
            backgroundColor: 'action.hover',
            boxShadow: (theme: any) => `inset 0 2px 6px ${alpha(theme.palette.common.black, 0.35)}`,
            border: 1,
            borderColor: 'divider',
            overflow: 'hidden',
            opacity: disableScanButton ? 0.5 : 1,
        },
        fill: {
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            width: `${dragX + THUMB_WIDTH + RAIL_PADDING * 2}px`,
            borderRadius: 1,
            backgroundColor: (theme: any) => alpha(theme.palette.secondary.main, 0.15 + progress * 0.35),
            transition: snapBack,
            pointerEvents: 'none',
        },
        label: {
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: `${THUMB_WIDTH + RAIL_PADDING * 2}px`,
            right: `${RAIL_PADDING * 2}px`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            lineHeight: 1.15,
            color: 'text.secondary',
            overflow: 'hidden',
            opacity: 1 - progress,
            transition: snapBack,
            pointerEvents: 'none',
            userSelect: 'none',
        },
        labelPrefix: {
            fontSize: 13,
            fontWeight: 500,
            lineHeight: 'inherit',
        },
        labelAction: {
            fontSize: { xs: 15, sm: 17 },
            fontWeight: 700,
            letterSpacing: 0.5,
            lineHeight: 'inherit',
        },
        thumb: {
            zIndex: 1,
            flexShrink: 0,
            minWidth: 0,
            width: `${THUMB_WIDTH}px`,
            height: '100%',
            borderRadius: 1,
            color: 'secondary.contrastText',
            backgroundColor: 'secondary.main',
            boxShadow: 3,
            cursor: 'grab',
            touchAction: 'none',
            transition: snapBack,
            '&:hover': {
                backgroundColor: 'secondary.main',
            },
            '&:active': {
                cursor: 'grabbing',
                backgroundColor: 'secondary.dark',
            },
            '&:disabled': {
                backgroundColor: 'action.disabledBackground',
                color: 'text.disabled',
                boxShadow: 0,
            },
        },
        thumbScanning: {
            '&:disabled': {
                backgroundColor: 'secondary.main',
                color: 'secondary.contrastText',
                opacity: 0.7,
            },
        },
    };

    const locked = machineActive || disableScanButton;

    return (
        <Grid sx={styles.display} item md={11} xs={11} pt={2}>
            <Box sx={styles.rail}>
                <Box sx={styles.fill} />
                <Box sx={styles.label}>
    {machineActive ? (
        <Typography component="span" sx={styles.labelAction}>
            {t('control.inputs.buttons.scanning')}
        </Typography>
    ) : isScanningCountdown ? (
        <Typography component="span" sx={styles.labelAction}>
            {scanButtonText}
        </Typography>
    ) : (
        <>
            <Typography component="span" sx={styles.labelPrefix}>
                {t('control.inputs.buttons.slideTo')}
            </Typography>
            <Typography component="span" sx={styles.labelAction}>
                {scanButtonText}
            </Typography>
        </>
    )}
</Box>
                <Draggable
                    axis="x"
                    bounds="parent"
                    nodeRef={thumbRef}
                    position={buttonPosition}
                    disabled={locked}
                    onStart={handleStart}
                    onDrag={handleDrag}
                    onStop={handleStop}
                >
                    <Button
                        ref={thumbRef}
                        aria-label={machineActive ? t('control.inputs.buttons.scanning') : scanButtonText}
                        sx={[styles.thumb, machineActive && styles.thumbScanning]}
                        disabled={locked}
                        disableRipple
                    >
                        {machineActive
                            ? <CircularProgress color="inherit" size={28} />
                            : <KeyboardDoubleArrowRightIcon fontSize="large" />}
                    </Button>
                </Draggable>
            </Box>
        </Grid>
    );
}